import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  AlertOctagon, Shield, Power, Clock, DollarSign, 
  Server, Database, Cloud, Zap, CheckCircle, XCircle,
  AlertTriangle, Lock, Unlock, History, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const SpendFreeze = () => {
  // Iter84al — ping real backend status on mount; auth-validated.
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/v2/spend-freeze/status`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
  }, []);
  const [isFrozen, setIsFrozen] = useState(false);
  const [freezeLoading, setFreezeLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [freezeHistory, setFreezeHistory] = useState([
    { id: 1, date: '2026-02-15', duration: '2h 15m', reason: 'Unexpected spike detected', savedAmount: 12400 },
    { id: 2, date: '2026-01-28', duration: '45m', reason: 'Manual freeze - budget review', savedAmount: 3200 },
  ]);

  const [affectedResources] = useState([
    { name: 'EC2 Instances', count: 47, monthlyCost: 12500, icon: Server, canFreeze: true },
    { name: 'RDS Databases', count: 8, monthlyCost: 4200, icon: Database, canFreeze: true },
    { name: 'Lambda Functions', count: 156, monthlyCost: 890, icon: Zap, canFreeze: true },
    { name: 'S3 Storage', count: 23, monthlyCost: 2100, icon: Cloud, canFreeze: false },
  ]);

  const totalMonthlyCost = affectedResources.reduce((sum, r) => sum + r.monthlyCost, 0);
  const freezeableCost = affectedResources.filter(r => r.canFreeze).reduce((sum, r) => sum + r.monthlyCost, 0);

  const handleFreeze = async () => {
    setFreezeLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsFrozen(true);
    setFreezeLoading(false);
    setShowConfirm(false);
    
    toast.success('🚨 SPEND FREEZE ACTIVATED - All scalable resources frozen', {
      duration: 5000,
    });
    
    // Add to history
    setFreezeHistory(prev => [{
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      duration: 'Active',
      reason: 'Manual emergency freeze',
      savedAmount: 0
    }, ...prev]);
  };

  const handleUnfreeze = async () => {
    setFreezeLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsFrozen(false);
    setFreezeLoading(false);
    toast.success('✅ Spend freeze lifted - Normal operations resumed');
  };

  const { shouldShowData } = useDemoData();
  if (!shouldShowData) return <EmptyDataState title="Spend Freeze" description="Connect your cloud accounts to enable emergency spending freeze controls." />;

  return (
    <div className="space-y-6" data-testid="spend-freeze-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertOctagon className="h-7 w-7 text-red-500" />
            Spend Freeze
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Emergency brake for your cloud spending. One click stops everything.
          </p>
        </div>
        <Badge className={isFrozen ? 'bg-red-100 text-red-700 border-red-200' : 'bg-green-100 text-green-700 border-green-200'}>
          {isFrozen ? '🔴 FROZEN' : '🟢 NORMAL'}
        </Badge>
      </div>

      {/* Main Freeze Button Card */}
      <Card className={`border-2 ${isFrozen ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
        <CardContent className="p-8">
          <div className="flex flex-col items-center text-center">
            {!isFrozen ? (
              <>
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-red-500/20 rounded-full blur-2xl animate-pulse"></div>
                  <button
                    onClick={() => setShowConfirm(true)}
                    disabled={freezeLoading}
                    className="relative w-40 h-40 rounded-full bg-red-500 bg-gradient-to-br from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 shadow-2xl shadow-red-500/50 flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95 group"
                    data-testid="freeze-button"
                  >
                    <div className="text-center">
                      <Power className="w-16 h-16 text-white mx-auto mb-1 group-hover:animate-pulse" />
                      <span className="text-white font-bold text-lg">FREEZE</span>
                    </div>
                  </button>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  Emergency Spend Freeze
                </h2>
                <p className="text-slate-400 dark:text-slate-400 max-w-md">
                  Instantly stop all scalable cloud resources. Use when costs are spiraling, 
                  during security incidents, or budget emergencies.
                </p>
              </>
            ) : (
              <>
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-red-500/30 rounded-full blur-2xl animate-pulse"></div>
                  <div className="relative w-40 h-40 rounded-full bg-red-600 bg-gradient-to-br from-red-600 to-red-800 shadow-2xl shadow-red-500/50 flex items-center justify-center border-4 border-red-400">
                    <div className="text-center">
                      <Lock className="w-16 h-16 text-white mx-auto mb-1 animate-pulse" />
                      <span className="text-white font-bold text-lg">FROZEN</span>
                    </div>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-2">
                  ⚠️ SPEND FREEZE ACTIVE
                </h2>
                <p className="text-red-600 dark:text-red-300 max-w-md mb-4">
                  All scalable resources are frozen. No new costs are being incurred.
                </p>
                <Button 
                  onClick={handleUnfreeze}
                  disabled={freezeLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-8"
                  data-testid="unfreeze-button"
                >
                  {freezeLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Unlock className="w-4 h-4 mr-2" />
                  )}
                  Lift Freeze
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-lg w-full border-2 border-red-500">
            <CardHeader className="bg-red-50 dark:bg-red-900/30">
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertTriangle className="w-6 h-6" />
                Confirm Spend Freeze
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-slate-400 dark:text-slate-300 mb-4">
                This will immediately freeze all scalable cloud resources:
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-400">
                  <XCircle className="w-4 h-4 text-red-500" />
                  Stop all EC2 auto-scaling
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-400">
                  <XCircle className="w-4 h-4 text-red-500" />
                  Pause Lambda function invocations
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-400">
                  <XCircle className="w-4 h-4 text-red-500" />
                  Freeze RDS scaling operations
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-400">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Storage (S3) will remain accessible
                </li>
              </ul>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Estimated hourly savings:</strong> ${(freezeableCost / 730).toFixed(2)}/hr
                </p>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowConfirm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleFreeze}
                  disabled={freezeLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {freezeLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <AlertOctagon className="w-4 h-4 mr-2" />
                  )}
                  Activate Freeze
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Affected Resources */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Resources Affected by Freeze</CardTitle>
          <CardDescription>These resources will be paused when freeze is activated</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {affectedResources.map((resource, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-lg border ${
                  resource.canFreeze 
                    ? 'border-slate-200 dark:border-slate-700' 
                    : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                } ${isFrozen && resource.canFreeze ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      resource.canFreeze 
                        ? 'bg-slate-100 dark:bg-slate-800' 
                        : 'bg-green-100 dark:bg-green-800'
                    }`}>
                      <resource.icon className={`w-5 h-5 ${
                        resource.canFreeze ? 'text-slate-400 dark:text-slate-400' : 'text-green-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{resource.name}</p>
                      <p className="text-sm text-slate-400">{resource.count} resources</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900 dark:text-white">
                      ${resource.monthlyCost.toLocaleString()}/mo
                    </p>
                    <Badge className={resource.canFreeze ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                      {resource.canFreeze ? (isFrozen ? 'Frozen' : 'Will freeze') : 'Protected'}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
            <span className="text-slate-400 dark:text-slate-400">Total freezeable spend</span>
            <span className="text-xl font-bold text-slate-900 dark:text-white">
              ${freezeableCost.toLocaleString()}/mo
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Freeze History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="w-5 h-5" />
            Freeze History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {freezeHistory.map((freeze) => (
              <div key={freeze.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <AlertOctagon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{freeze.reason}</p>
                    <p className="text-sm text-slate-400">{freeze.date} • Duration: {freeze.duration}</p>
                  </div>
                </div>
                {freeze.savedAmount > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    Saved ${freeze.savedAmount.toLocaleString()}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SpendFreeze;
